import { ThemeColorName } from '@examdojo/styles';
import type { ActionDefinition } from './widget-action.model';

// ==============================================================================
// UI CONTROL SYSTEM (The bindings)
// Maps external UI elements to internal Actions.
// ==============================================================================

/** Control types supported by the widget system */
export enum WidgetControlType {
  Button = 'button',
  ButtonGroup = 'button-group',
  Slider = 'slider',
  SliderGroup = 'slider-group',
  MathInput = 'math-input',
}

/** Where controls can be rendered */
export type WidgetControlPosition = 'top-bar' | 'bottom-bar' | 'canvas-overlay';

/** Base control properties shared by all control types */
export interface WidgetControlBase<TType extends WidgetControlType = WidgetControlType> {
  /** Control type discriminator */
  type: TType;
  /** Unique ID for DOM keys */
  id: string;
  /** Human-readable label */
  label: string;
  /** Where to render this control */
  position?: WidgetControlPosition;
}

/**
 * Button control - triggers an action on click
 * Variants: text-only (no icon), icon-only (icon + showLabel:false), text+icon (icon + showLabel:true)
 */
export interface WidgetButtonControl extends WidgetControlBase<WidgetControlType.Button> {
  /** FontAwesome icon name (e.g. 'magnifying-glass-plus') */
  icon?: string;
  /** Whether to show the label text. Default: true if no icon, false if icon present */
  showLabel?: boolean;
  /** The Action to fire when clicked */
  trigger: ActionDefinition;
}

/** Button group control - renders as a segment (grouped related buttons) */
export interface WidgetButtonGroupControl extends WidgetControlBase<WidgetControlType.ButtonGroup> {
  /** Buttons in this group */
  buttons: Array<Omit<WidgetButtonControl, 'type' | 'position'>>;
}

/** Slider control - numeric range input with optional editable input field */
export interface WidgetSliderControl extends WidgetControlBase<WidgetControlType.Slider> {
  /** Parameter name to display (e.g., "a" shows "a = 2.5") */
  param: string;
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Step increment */
  step?: number;
  /** Default value */
  defaultValue?: number;
  /** Whether to show an editable input box for direct value entry (default: false) */
  editable?: boolean;
  /** Color for the slider handle and trace (CSS color value, e.g., '#0d95ea' or 'var(--sky-500)') */
  color?: ThemeColorName;
  /** The Action to fire when value changes. Payload will include { value: number } */
  trigger: ActionDefinition;
}

/** Slider group control - multiple sliders stacked in a styled container */
export interface WidgetSliderGroupControl extends WidgetControlBase<WidgetControlType.SliderGroup> {
  /** Sliders in this group */
  sliders: Array<Omit<WidgetSliderControl, 'type' | 'position'>>;
}

/** Math input control - input field only (no slider) for numeric values */
export interface WidgetMathInputControl extends WidgetControlBase<WidgetControlType.MathInput> {
  /** Parameter name to display (e.g., "a" shows "a = [input]") */
  param: string;
  /** Default value */
  defaultValue?: number;
  /** The Action to fire when value changes. Payload will include { value: number } */
  trigger: ActionDefinition;
}

/** Union of all control types */
export type WidgetControl =
  | WidgetButtonControl
  | WidgetButtonGroupControl
  | WidgetSliderControl
  | WidgetSliderGroupControl
  | WidgetMathInputControl;
